import { Archive, CircleCheck, Hourglass, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { SkeletonLines } from '@/components/ui/Skeleton';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { PlatformStats } from '@/types/institute';
import { useReveal } from './useReveal';

interface FleetCompositionProps {
  stats: PlatformStats | null;
  isLoading: boolean;
}

export function FleetComposition({ stats, isLoading }: FleetCompositionProps) {
  const isRevealed = useReveal();

  const total = stats?.totalInstitutes ?? 0;
  const active = stats?.activeInstitutes ?? 0;
  const pending = stats?.pendingSetup ?? 0;
  const suspended = stats?.suspendedInstitutes ?? 0;
  const closed = Math.max(0, total - active - pending - suspended);

  const bands = [
    {
      key: 'ACTIVE',
      label: 'Active',
      // The account status, not a measure of use — so it claims no more than that.
      hint: 'Live customers',
      count: active,
      barBg: 'bg-blue-600',
      dot: 'bg-blue-500',
      text: 'text-blue-600 dark:text-blue-400',
      icon: CircleCheck,
      cardBg: 'border-blue-100 dark:border-blue-900/50 bg-gradient-to-b from-white to-blue-50/60 dark:from-slate-900 dark:to-blue-950/20 shadow-[0_2px_8px_rgba(59,130,246,0.05)]',
      to: '/platform/institutes?status=ACTIVE',
    },
    {
      key: 'PENDING_SETUP',
      label: 'Pending setup',
      hint: 'Invited, not finished onboarding',
      count: pending,
      barBg: 'bg-amber-400',
      dot: 'bg-amber-400',
      text: 'text-amber-600 dark:text-amber-400',
      icon: Hourglass,
      cardBg: 'border-amber-100 dark:border-amber-900/50 bg-gradient-to-b from-white to-amber-50/60 dark:from-slate-900 dark:to-amber-950/20 shadow-[0_2px_8px_rgba(245,158,11,0.05)]',
      to: '/platform/institutes?status=PENDING_SETUP',
    },
    {
      key: 'SUSPENDED',
      label: 'Suspended',
      hint: 'Every login stopped, data untouched',
      count: suspended,
      barBg: 'bg-rose-500',
      dot: 'bg-rose-500',
      text: 'text-rose-600 dark:text-rose-400',
      icon: ShieldAlert,
      cardBg: 'border-rose-100 dark:border-rose-900/50 bg-gradient-to-b from-white to-rose-50/60 dark:from-slate-900 dark:to-rose-950/20 shadow-[0_2px_8px_rgba(244,63,94,0.05)]',
      to: '/platform/institutes?status=SUSPENDED',
    },
    {
      key: 'ARCHIVED',
      label: 'Archived',
      hint: 'Closed accounts, kept for the record',
      count: closed,
      barBg: 'bg-slate-400',
      dot: 'bg-slate-400',
      text: 'text-slate-500',
      icon: Archive,
      cardBg: 'border-slate-200 dark:border-slate-800 bg-gradient-to-b from-white to-slate-50/70 dark:from-slate-900 dark:to-slate-800/20 shadow-[0_2px_8px_rgba(0,0,0,0.03)]',
      to: '/platform/institutes?status=ARCHIVED',
    },
  ].filter((band) => band.count > 0);

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-bold text-slate-900 dark:text-white">
            How the customer base stands
          </h2>
          <p className="mt-0.5 text-[12px] text-slate-500 dark:text-slate-400 font-medium">
            Every institute on the platform, by the state its account is in.
          </p>
        </div>
        {!isLoading && total > 0 && (
          <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11.5px] font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
            {formatNumber(total)} total
          </span>
        )}
      </div>

      {isLoading ? (
        <SkeletonLines count={3} />
      ) : total === 0 ? (
        <p className="py-4 text-center text-[13px] text-slate-400">
          No institutes yet. The first customer you add appears here.
        </p>
      ) : (
        <>
          {/* Multi-status progress bar */}
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            {bands.map((band) => {
              const pct = (band.count / total) * 100;
              return (
                <div
                  key={band.key}
                  className={cn('h-full transition-all duration-700 ease-out', band.barBg)}
                  style={{ width: isRevealed ? `${pct}%` : '0%' }}
                />
              );
            })}
          </div>

          {/* Status cards */}
          <ul className="grid gap-2.5 sm:grid-cols-2">
            {bands.map((band) => {
              const Icon = band.icon;
              const share = Math.round((band.count / total) * 100);

              return (
                <li key={band.key}>
                  <Link
                    to={band.to}
                    className={cn(
                      'group flex items-center gap-2.5 rounded-xl border p-2.5 transition-all hover:scale-[1.01] hover:shadow-sm',
                      band.cardBg,
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white dark:bg-slate-900 shadow-2xs',
                        band.text,
                      )}
                      aria-hidden
                    >
                      <Icon className="h-4 w-4" />
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', band.dot)} aria-hidden />
                        <span className="truncate text-[13px] font-bold text-slate-800 dark:text-slate-100">
                          {band.label}
                        </span>
                      </span>
                      <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500 font-medium">{band.hint}</span>
                    </span>

                    <span className="shrink-0 text-right">
                      <span className="block text-[13.5px] font-black tabular-nums text-slate-900 dark:text-white">
                        {formatNumber(band.count)}
                      </span>
                      <span className="block text-[11px] tabular-nums text-slate-400 font-bold">{share}%</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
